import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  X,
  Image,
  Video,
  Trash2,
  Upload,
  FolderOpen,
  Search,
  Check,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { uploadApi } from '../api/client';
import type { MediaItem } from '../api/modules/upload';

interface MediaUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Optional for platform assets (e.g. How To guides) that are not site-scoped */
  siteId?: string;
  mediaType: 'image' | 'video';
  componentType?: string;
  assetType?:
    | 'logo'
    | 'background'
    | 'image'
    | 'icon'
    | 'thumbnail'
    | 'video'
    | 'gallery'
    | 'image-info'
    | 'image-card'
    | 'media';
  currentMediaUrl?: string;
  onMediaUploaded: (
    mediaUrl: string,
    cloudfrontUrl?: string,
    mediaId?: string,
    metadata?: {
      mimeType: string;
      duration?: number;
      width?: number;
      height?: number;
      fileSize: number;
    },
    s3Key?: string
  ) => void;
}

/**
 * Format bytes to human-readable string
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

export const MediaUploadModal: React.FC<MediaUploadModalProps> = ({
  isOpen,
  onClose,
  siteId,
  mediaType,
  componentType = 'media-text',
  assetType,
  currentMediaUrl,
  onMediaUploaded,
}) => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'upload' | 'library'>('upload');

  // Upload tab state
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentMediaUrl || null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Large file warning state
  const [largeFileWarning, setLargeFileWarning] = useState<{
    file: File;
    size: number;
  } | null>(null);
  const LARGE_FILE_THRESHOLD = 500 * 1024; // 500KB

  // Library tab state
  const [libraryMedia, setLibraryMedia] = useState<MediaItem[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLibraryMedia, setSelectedLibraryMedia] = useState<MediaItem | null>(null);

  // Load library media when tab switches to library
  useEffect(() => {
    if (isOpen && activeTab === 'library') {
      const fetchLibraryMedia = async () => {
        try {
          setIsLoadingLibrary(true);
          setLibraryError(null);
          const data = await uploadApi.getMedia(siteId);
          // Filter by media type
          const filtered = data.filter(item => {
            if (mediaType === 'image') return item.mimeType.startsWith('image/');
            if (mediaType === 'video') return item.mimeType.startsWith('video/');
            return true;
          });
          setLibraryMedia(filtered);
        } catch (err) {
          console.error('Failed to fetch media library:', err);
          setLibraryError('Failed to load media library');
        } finally {
          setIsLoadingLibrary(false);
        }
      };
      fetchLibraryMedia();
    }
  }, [isOpen, activeTab, siteId, mediaType]);

  // Filter library media by search
  const filteredLibraryMedia = useMemo(() => {
    if (!searchQuery) return libraryMedia;
    return libraryMedia.filter(item =>
      item.filename.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [libraryMedia, searchQuery]);

  // Handle selecting media from library
  const handleSelectFromLibrary = () => {
    if (!selectedLibraryMedia) return;

    onMediaUploaded(
      selectedLibraryMedia.url,
      selectedLibraryMedia.cloudfrontUrl,
      selectedLibraryMedia.id,
      {
        mimeType: selectedLibraryMedia.mimeType,
        fileSize: selectedLibraryMedia.size,
      },
      selectedLibraryMedia.s3Key
    );
    onClose();
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('upload');
      setSelectedLibraryMedia(null);
      setSearchQuery('');
      setError(null);
      setLibraryError(null);
      setLargeFileWarning(null);
    }
  }, [isOpen]);

  // Function to extract video metadata
  const extractVideoMetadata = (
    file: File
  ): Promise<{
    duration: number;
    width: number;
    height: number;
  }> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve({
          duration: Math.round(video.duration || 0),
          width: video.videoWidth || 0,
          height: video.videoHeight || 0,
        });
      };

      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = (file: File) => {
    setError(null);
    setLargeFileWarning(null);

    // Validate file type
    const isValidType =
      mediaType === 'image' ? file.type.startsWith('image/') : file.type.startsWith('video/');

    if (!isValidType) {
      setError(`Please select a ${mediaType} file`);
      return;
    }

    // Check for large image files and show warning (videos are expected to be larger)
    if (mediaType === 'image' && file.size > LARGE_FILE_THRESHOLD) {
      setLargeFileWarning({ file, size: file.size });
      // Show preview but don't upload yet
      if (file.size < 50 * 1024 * 1024) {
        const reader = new FileReader();
        reader.onload = e => {
          setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
      return; // Wait for user to confirm
    }

    // Proceed with upload
    proceedWithUpload(file);
  };

  const proceedWithUpload = (file: File) => {
    setLargeFileWarning(null);

    // Show preview for smaller files (under 50MB to avoid memory issues)
    if (file.size < 50 * 1024 * 1024) {
      const reader = new FileReader();
      reader.onload = e => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      // For large files, show a placeholder or the filename
      setPreviewUrl(null);
    }

    // Upload file
    uploadFile(file);
  };

  const handleLargeFileConfirm = () => {
    if (largeFileWarning) {
      proceedWithUpload(largeFileWarning.file);
    }
  };

  const handleLargeFileCancel = () => {
    setLargeFileWarning(null);
    setPreviewUrl(null);
  };

  const uploadFile = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadProgress(0);
      setError(null);

      console.log('=== Media Upload Debug ===');
      console.log(
        'Backend URL:',
        import.meta.env.VITE_API_BASE_URL || 'https://compete-pages-api.vercel.app'
      );
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size,
        lastModified: new Date(file.lastModified).toISOString(),
      });

      // Determine the correct assetType to use for upload
      // If explicitly provided, use it; otherwise default to mediaType
      const uploadAssetType = assetType || mediaType;

      console.log('Upload params:', {
        siteId,
        componentType: componentType,
        assetType: uploadAssetType,
      });

      // Use presigned URL upload with real progress tracking
      // This supports unlimited file sizes and provides accurate progress
      const response = await uploadApi.uploadWithPresignedUrl(file, {
        siteId,
        componentType,
        assetType: uploadAssetType,
        onProgress: progress => {
          setUploadProgress(progress);
        },
      });

      setUploadProgress(100);

      console.log('Upload successful:', response);

      // Extract metadata for video files
      let metadata = {
        mimeType: response.mimeType,
        fileSize: response.size,
        duration: undefined as number | undefined,
        width: undefined as number | undefined,
        height: undefined as number | undefined,
      };

      if (mediaType === 'video') {
        try {
          const videoMetadata = await extractVideoMetadata(file);
          metadata = {
            ...metadata,
            duration: videoMetadata.duration,
            width: videoMetadata.width,
            height: videoMetadata.height,
          };
          console.log('Extracted video metadata:', videoMetadata);
        } catch (error) {
          console.warn('Failed to extract video metadata:', error);
        }
      }

      // Update the parent component with the new media URL and metadata
      onMediaUploaded(response.url, response.cloudfrontUrl, response.id, metadata, response.s3Key);

      // Close modal after a brief delay to show completion
      setTimeout(() => {
        onClose();
        setUploadProgress(0);
        setPreviewUrl(null);
      }, 500);
    } catch (err: any) {
      console.error('Media upload failed:', err);
      console.error('Full error object:', err);
      console.error('Error status:', err.status);
      console.error('Error data:', err.data);

      let errorMessage = `Failed to upload ${mediaType}`;

      if (err.status === 500) {
        errorMessage = `Internal server error while uploading ${mediaType}. Please try again or contact support.`;
      } else if (err.status === 415) {
        errorMessage = `Unsupported file type. Please use ${
          mediaType === 'video' ? 'MP4, WebM, or OGG' : 'PNG, JPG, or WebP'
        } format.`;
      } else if (err.status === 400) {
        errorMessage = err.message || `Invalid ${mediaType} file or missing required information.`;
      } else if (err.status === 401) {
        errorMessage = 'Authentication required. Please log in again.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveMedia = () => {
    setPreviewUrl(null);
    onMediaUploaded(
      '',
      undefined,
      undefined,
      {
        mimeType: '',
        fileSize: 0,
      },
      undefined
    );
    onClose();
  };

  if (!isOpen) return null;

  const acceptedTypes =
    mediaType === 'image'
      ? 'image/jpeg,image/jpg,image/png,image/webp'
      : 'video/mp4,video/webm,video/ogg';

  const supportedFormats =
    mediaType === 'image' ? 'PNG, JPG, or WebP (any size)' : 'MP4, WebM, or OGG (any size)';

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[80]">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg border border-gray-100 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center space-x-2">
              {mediaType === 'image' ? <Image size={20} /> : <Video size={20} />}
              <span>Select {mediaType === 'image' ? 'Image' : 'Video'}</span>
            </h2>
            <p className="text-sm text-gray-500 mt-1">Upload new or choose from your library</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
            disabled={isUploading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            type="button"
            onClick={() => setActiveTab('upload')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
              activeTab === 'upload'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <Upload size={16} />
            <span>Upload New</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('library')}
            className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
              activeTab === 'library'
                ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FolderOpen size={16} />
            <span>Choose from Library</span>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-1 overflow-y-auto">
          {/* Upload Tab Content */}
          {activeTab === 'upload' && (
            <>
              {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <X size={20} className="text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Upload Error</h3>
                      <div className="mt-2 text-sm text-red-700">
                        <pre className="whitespace-pre-wrap font-sans">{error}</pre>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Large File Warning */}
              {largeFileWarning && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle size={20} className="text-yellow-500" />
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="text-sm font-medium text-yellow-800">Large Image Detected</h3>
                      <div className="mt-2 text-sm text-yellow-700">
                        <p>
                          This image is {formatFileSize(largeFileWarning.size)}, which may slow down
                          your page load times. For best performance, images under 500KB are
                          recommended.
                        </p>
                      </div>
                      <div className="mt-4 flex space-x-3">
                        <button
                          type="button"
                          onClick={handleLargeFileConfirm}
                          className="px-3 py-1.5 text-sm font-medium text-yellow-800 bg-yellow-100 hover:bg-yellow-200 rounded-md transition-colors"
                        >
                          Upload Anyway
                        </button>
                        <button
                          type="button"
                          onClick={handleLargeFileCancel}
                          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-colors"
                        >
                          Choose Different Image
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Current/Preview Media */}
              {previewUrl && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current {mediaType === 'image' ? 'Image' : 'Video'}
                  </label>
                  <div className="relative inline-block">
                    {mediaType === 'image' ? (
                      <img
                        src={previewUrl}
                        alt="Media preview"
                        className="w-32 h-24 rounded-lg object-cover border-2 border-gray-200"
                      />
                    ) : (
                      <video
                        src={previewUrl}
                        className="w-32 h-24 rounded-lg object-cover border-2 border-gray-200"
                        controls={false}
                        muted
                      />
                    )}
                    <button
                      type="button"
                      onClick={handleRemoveMedia}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      title={`Remove ${mediaType}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700">
                  {previewUrl
                    ? `Change ${mediaType === 'image' ? 'Image' : 'Video'}`
                    : `Upload ${mediaType === 'image' ? 'Image' : 'Video'}`}
                </label>

                <div
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDragging
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  } ${isUploading ? 'opacity-50 pointer-events-none' : ''}`}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedTypes}
                    onChange={handleFileInputChange}
                    className="hidden"
                  />

                  {isUploading ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">Uploading {mediaType}...</p>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className="text-xs text-gray-500">{uploadProgress}% complete</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center justify-center">
                        <div className="p-3 bg-gray-100 rounded-full">
                          {mediaType === 'image' ? (
                            <Image size={24} className="text-gray-600" />
                          ) : (
                            <Video size={24} className="text-gray-600" />
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm text-gray-600">
                          Drop a {mediaType} here, or{' '}
                          <button
                            type="button"
                            onClick={handleBrowseClick}
                            className="text-primary-600 hover:text-primary-700 font-medium"
                          >
                            browse
                          </button>
                        </p>
                        <p className="text-xs text-gray-500">{supportedFormats}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Tips */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 mb-2">Tips for best results:</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  {mediaType === 'image' ? (
                    <>
                      <li>• Use high-quality images for best results</li>
                      <li>• Recommended minimum size: 800x600 pixels</li>
                      <li>• Any file size is supported</li>
                      <li>• Large files may take longer to upload</li>
                    </>
                  ) : (
                    <>
                      <li>• Use MP4 format for best compatibility</li>
                      <li>• Any file size is supported</li>
                      <li>• Large files may take longer to upload</li>
                      <li>• Consider adding a poster image for better UX</li>
                    </>
                  )}
                </ul>
              </div>
            </>
          )}

          {/* Library Tab Content */}
          {activeTab === 'library' && (
            <>
              {libraryError && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">{libraryError}</p>
                </div>
              )}

              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Search by filename..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Media Grid */}
              {isLoadingLibrary ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 size={32} className="text-primary-600 animate-spin mb-4" />
                  <p className="text-gray-500">Loading media...</p>
                </div>
              ) : filteredLibraryMedia.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <FolderOpen size={48} className="text-gray-300 mb-4" />
                  <p className="text-gray-700 font-medium mb-2">No {mediaType}s found</p>
                  <p className="text-gray-500 text-sm">
                    {searchQuery
                      ? 'Try a different search term'
                      : 'Upload some media to get started'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto">
                  {filteredLibraryMedia.map(item => {
                    const selected = selectedLibraryMedia?.id === item.id;
                    const isItemImage = item.mimeType.startsWith('image/');

                    return (
                      <div
                        key={item.id}
                        onClick={() => setSelectedLibraryMedia(item)}
                        className={`relative rounded-lg border-2 overflow-hidden cursor-pointer transition-all ${
                          selected
                            ? 'border-primary-500 ring-2 ring-primary-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="aspect-square bg-gray-100 relative">
                          {isItemImage ? (
                            <img
                              src={item.cloudfrontUrl || item.url}
                              alt={item.filename}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-800">
                              <Video size={24} className="text-white" />
                            </div>
                          )}

                          {selected && (
                            <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center">
                              <div className="w-6 h-6 rounded-full bg-primary-500 flex items-center justify-center">
                                <Check size={14} className="text-white" />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="p-2">
                          <p
                            className="text-xs font-medium text-gray-700 truncate"
                            title={item.filename}
                          >
                            {item.filename}
                          </p>
                          <p className="text-xs text-gray-400">{formatFileSize(item.size)}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-100">
          <button type="button" onClick={onClose} className="btn-secondary" disabled={isUploading}>
            Cancel
          </button>
          {activeTab === 'library' && (
            <button
              type="button"
              onClick={handleSelectFromLibrary}
              disabled={!selectedLibraryMedia}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Use Selected
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
